import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { ReactElement, useEffect, useState } from 'react';
import { PublicSubmissionService } from 'services/PublicSubmissionService';
import { Utils } from 'shared/helpers';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useNavigate } from 'react-router-dom';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

export interface PublicSubmissionProps {
  classification: any;
  stakeholder: any;
  legalEntity: any;
  businessProcesses: any;
  technicalDocumentationLink: any;
  vendorName: any;
  consumptionMethod: any;
  environment: any;
  watermarkingTechnique: any;
  lifecycleStage: any;
  versionControlMethod: any;
  decisionCapability: any;
  buildDate: any;
  lastTrainingDate: any;
  _id: string;
  sourceType: string;
  solutionName: string;
  solutionOwner: string;
  organizationUnit: string;
  organizationSubUnit: string;
  userCustomerName: string;
  uses: string;
  purpose: string;
  businessGoals: string;
  businessValue: string;
  platformName: string;
  riskTier: string;
  openSource: boolean;
  aiModelTypes: any[];
  organizationId: string;
  datasetId: any[];
  tenantId: any;
  createdBy: any;
  updatedBy: any;
  updatedDate: any;
  isActive: boolean;
  deletedAt: any;
  publicSubmissionMail: string;
  isPublic: boolean;
  isPublicApprovalStatus: string;
  pubReqApprovedBy: any;
  convertedBy: any;
  isConverted: boolean;
  createdDate: string;
  __v: number;
}

const PublicSubmisstion = (): ReactElement => {
  const [publicSubmissions, setPublicSubmissions] = useState<
    PublicSubmissionProps[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const getSubmissionList = (): void => {
    setIsLoading(true);

    PublicSubmissionService.getPublicSubmissionList()
      .then((res) => {
        const { data } = res.data;
        setPublicSubmissions(data);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const actionOnOpen = (id: string): void => {
    navigate(`/ai-systems/records/${id}`);
  };

  const actionOnUpdateSubmission = (id: string, status: string): void => {
    PublicSubmissionService.updateSubmissionList(id, status)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        getSubmissionList();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const actionConvertSubmission = (id: string): void => {
    PublicSubmissionService.convertSubmission(id)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        getSubmissionList();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  useEffect(() => {
    getSubmissionList();
  }, []);

  return (
    <Box>
      <Card className="mt-10 p-5">
        {isLoading && publicSubmissions.length === 0 && (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        )}
        {publicSubmissions.length > 0 && !isLoading && (
          <>
            <Typography
              variant="h3"
              className="mb-2"
              sx={{
                fontSize: {
                  xs: '20px',
                  sm: '24px',
                },
              }}
            >
              Public Submission ({publicSubmissions.length})
            </Typography>
            <Grid container spacing={2}>
              {publicSubmissions.map((submission) => (
                <Grid item xs={12}>
                  <Card
                    className=" p-5"
                    sx={{
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: {
                          xs: 'column',
                          sm: 'row',
                        },
                        alignItems: {
                          xs: 'flex-start',
                          md: 'center',
                        },
                        justifyContent: 'space-between',
                        gap: {
                          xs: 1.5,
                          md: 2,
                        },
                        width: '100%',
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: {
                            xs: 'flex-start',
                            sm: 'center',
                          },
                          flexDirection: {
                            xs: 'column',
                            sm: 'row',
                          },
                          gap: {
                            xs: 1,
                            sm: 0,
                          },
                          minWidth: 0,
                          width: {
                            xs: '100%',
                            md: 'auto',
                          },
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: {
                              xs: '18px',
                              sm: '20px',
                            },
                            lineHeight: 1.4,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {submission.solutionName}
                        </Typography>
                        {submission.isPublicApprovalStatus === 'Pending' && (
                          <Chip
                            label="Pending"
                            className="mx-2 cursor-pointer"
                            sx={{ background: '#c2c2c2', color: 'black' }}
                            size="small"
                          />
                        )}{' '}
                        {submission.isPublicApprovalStatus === 'Approved' && (
                          <Chip
                            label="Approved"
                            className="mx-2 cursor-pointer"
                            sx={{ background: '#4CAF50', color: 'white' }}
                            size="small"
                          />
                        )}
                        {submission.isPublicApprovalStatus === 'Rejected' && (
                          <Chip
                            label="Rejected"
                            className="mx-2 cursor-pointer"
                            sx={{ background: '#f44336', color: 'white' }}
                            size="small"
                          />
                        )}
                      </Box>
                      <Box className="align-center">
                        {orgACLConfig.aiInventory?.includes(
                          'aiInventory.aiSystem.publicSubmissions.view'
                        ) && (
                          <RemoveRedEyeOutlinedIcon
                            fontSize="small"
                            className="p-1 mx-1"
                            sx={{
                              border: '1px solid #d7d6d6',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              actionOnOpen(submission._id);
                            }}
                          />
                        )}
                        {submission.isPublicApprovalStatus === 'Pending' && (
                          <>
                            {orgACLConfig.aiInventory?.includes(
                              'aiInventory.aiSystem.publicSubmissions.approve'
                            ) && (
                              <CheckOutlinedIcon
                                fontSize="small"
                                className="p-1 mx-1"
                                sx={{
                                  border: '1px solid #d7d6d6',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  actionOnUpdateSubmission(
                                    submission._id,
                                    'Approved'
                                  );
                                }}
                              />
                            )}
                            {orgACLConfig.aiInventory?.includes(
                              'aiInventory.aiSystem.publicSubmissions.reject'
                            ) && (
                              <CloseOutlinedIcon
                                fontSize="small"
                                className="p-1 mx-1"
                                sx={{
                                  border: '1px solid #d7d6d6',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  actionOnUpdateSubmission(
                                    submission._id,
                                    'Rejected'
                                  );
                                }}
                              />
                            )}
                          </>
                        )}
                        {submission.isPublicApprovalStatus === 'Approved' &&
                          !submission.isConverted && (
                            <Chip
                              label="Convert to Inventory"
                              className="mx-2 cursor-pointer"
                              sx={{ background: 'black', color: 'white' }}
                              size="small"
                              onClick={() => {
                                actionConvertSubmission(submission._id);
                              }}
                            />
                          )}
                      </Box>
                    </Box>
                    <Typography variant="body1">
                      Submitted :{' '}
                      {moment(submission.createdDate).format(
                        'M/D/YYYY, h:mm:ss A'
                      )}
                    </Typography>
                    <Typography variant="body1">
                      Contact : {submission.publicSubmissionMail}
                    </Typography>
                    <Typography variant="body1">
                      Purpose : {submission.purpose}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {!isLoading && publicSubmissions.length === 0 && (
          <Box className="empty-content">
            <Typography className="f18 text-gray">
              No public submissions
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default PublicSubmisstion;
